import { Component, Host, h, Element, Prop, State } from "@stencil/core";
import { getPlaceholderUrl } from "../../utils/getPlaceholderUrl";

/**
 * @slot image - insert the image element. As default, the zoom effect will be applied to the image element, if
 * it has to be applied to an element that is not an image, give the element the class "ux-iws-teaser-image__image-alternative"
 * @slot (default) - insert a title and an intro text
 * @slot tags - insert tags element in a wrapped in a div container
 * @slot label - insert a label
 * @slot headline - insert a headline
 * @slot text - insert the copy text
 * @slot cta - insert a cta button
 */

@Component({
    tag: "ux-iws-teaser-image",
    styleUrl: "teaser-image.scss",
    shadow: false,
})
export class TeaserImage {
    private baseClass = "ux-iws-teaser-image";

    @Element() element: HTMLUxIwsTeaserImageElement;
    /** the href where the teaser should link to */
    @Prop() href?: string = null;
    /** this will be passed to the a element as target attribute */
    @Prop() target?: string = null;
    /** if set, the text of the teaser and the cta will be aligned in a row */
    @Prop() textCtaRow: boolean = false;
    /** if set, the component will have a box shadow */
    @Prop() noShadow: boolean = false;
    /** if set, the component will have a box shadow */
    @Prop() imageRatio: "4:3" | "17:8" | "8:7" = "4:3";
    /** if set, the component will show a green border */
    @Prop() greenBorder: boolean = false;
    /** if set, all the elements of the slot="text" will be set as inline */
    @Prop() inlineTextSlot: boolean = false;
    /** if set, the image passed will be centered and will have height set to auto */
    @Prop() imageAutoHeight: boolean = false;
    /** if set, the flex direction of the text-wrapper will be set to column */
    @Prop() textWrapperColumn: boolean = false;
    /** if set, the element in the cta slot will be aligned to the top.
     * Does not have any effect if the propety textWrapperColumn is set to true.
     */
    @Prop() alignCtaTop: boolean = false;

    @State() hasNoImage: boolean = false;

    componentWillLoad() {
        const slottedImage = this.element.querySelector("*[slot='image']");
        if (!slottedImage) this.hasNoImage = true;
    }

    render() {
        const imageContainerHeight =
            this.imageRatio === "17:8"
                ? 47.05
                : this.imageRatio === "8:7"
                ? 87.5
                : 56.25;
        const placeholderDims: Array<number> =
            this.imageRatio === "17:8"
                ? [17, 8]
                : this.imageRatio === "8:7"
                ? [8, 7]
                : [4, 3];
        return (
            <Host
                class={{
                    [this.baseClass]: true,
                    [`${this.baseClass}--green-border`]: this.greenBorder,
                    ["ux-u-shadow"]: !this.noShadow,
                }}
            >
                <ux-iws-link-wrapper>
                    {this.href !== null ? (
                        <a
                            href={this.href}
                            target={this.target}
                            class={{
                                [`${this.baseClass}__image-wrapper`]: true,
                                [`${this.baseClass}__image-wrapper--auto-height`]: this
                                    .imageAutoHeight,
                            }}
                            style={{ paddingTop: imageContainerHeight + "%" }}
                        >
                            <slot name="image" />
                            {this.hasNoImage && (
                                <img
                                    src={getPlaceholderUrl(
                                        placeholderDims[0],
                                        placeholderDims[1]
                                    )}
                                    alt="TRUMPF logo"
                                />
                            )}
                        </a>
                    ) : (
                        <div
                            class={{
                                [`${this.baseClass}__image-wrapper`]: true,
                                [`${this.baseClass}__image-wrapper--auto-height`]: this
                                    .imageAutoHeight,
                            }}
                            style={{ paddingTop: imageContainerHeight + "%" }}
                        >
                            <slot name="image" />
                            {this.hasNoImage && (
                                <img
                                    src={getPlaceholderUrl(
                                        placeholderDims[0],
                                        placeholderDims[1]
                                    )}
                                    alt="TRUMPF logo"
                                />
                            )}
                        </div>
                    )}
                    <div
                        class={{
                            [`${this.baseClass}__info-wrapper`]: true,
                            [`${this.baseClass}__info-wrapper--space-between`]: this
                                .alignCtaTop,
                        }}
                    >
                        <slot />
                        <div class={`${this.baseClass}__tag-container`}>
                            <slot name="tags"></slot>
                        </div>
                        <slot name="label"></slot>
                        <slot name="headline"></slot>
                        <div
                            class={{
                                [`${this.baseClass}__text-wrapper`]: true,
                                [`${this.baseClass}__text-wrapper--row`]: !this
                                    .textWrapperColumn,
                                [`${this.baseClass}__text-wrapper--column`]: this
                                    .textWrapperColumn,
                                [`${this.baseClass}__text-wrapper--inline-text`]: this
                                    .inlineTextSlot,
                                [`${this.baseClass}__text-wrapper--align-cta-top`]: this
                                    .alignCtaTop,
                            }}
                        >
                            <slot name="text"></slot>
                            <slot name="cta"></slot>
                        </div>
                    </div>
                </ux-iws-link-wrapper>
            </Host>
        );
    }
}
